import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useFormPopupContext } from '../../contexts/FormPopupContext';
import { products } from '../../utils/data';
import HeroSection from '../../components/HeroSection';

const ImmersionSurround: React.FC = () => {
  const product = products.find(p => p.id === 'immersion-surround');
  const { openFormPopup } = useFormPopupContext();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  if (!product) {
    return <div>Product not found</div>;
  }

  return (
    <div className="min-h-screen">
      {/* CINEMATIC HERO SECTION */}
      <HeroSection
        desktopImage="/images/hero-section-5.png"
        mobileImage="/images/mobile-hero-5.png"
        title={
          <>
            <span className="text-gradient block">Parallax Immersion</span>
            <span className="block text-3xl md:text-4xl text-secondary mt-2 font-normal">
              5.1 Surround System
            </span>
          </>
        }
        subtitle="Immerse yourself in sonic perfection. Our flagship surround system delivers reference-quality audio that transforms any space into a premium cinema, placing you at the heart of every performance."
        tagline={`Investment: ${product.priceRange}`}
      >
        <motion.div 
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7 }}
        >
          <button 
            onClick={openFormPopup}
            className="btn-primary text-base sm:text-lg whitespace-nowrap"
          >
            Experience The Sound
          </button>
          <button 
            onClick={openFormPopup}
            className="btn-secondary text-base sm:text-lg whitespace-nowrap"
          >
            Schedule Acoustic Consultation
          </button>
        </motion.div>
      </HeroSection>

      {/* ACOUSTIC PHILOSOPHY SECTION */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl md:text-5xl font-light mb-8 text-gradient">
                  Handcrafted Acoustic Artistry
                </h2>
                <div className="space-y-6 text-lg text-secondary/90 leading-relaxed">
                  <p>
                    Each Immersion speaker is meticulously crafted using proprietary acoustic 
                    technology and premium materials. Our master craftsmen hand-select every 
                    component, creating a soundstage that places you at the conductor's podium.
                  </p>
                  <p>
                    Experience the subtle breath of a vocalist, the delicate resonance of strings, 
                    and the thunderous power of orchestral crescendos with reference-grade 
                    frequency response that reveals every nuance.
                  </p>
                  <p>
                    This isn't merely a speaker system—it's a handcrafted acoustic environment 
                    that transforms your space into a sanctuary of sound.
                  </p>
                </div>
              </motion.div>
              
              <motion.div 
                className="relative max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                {/* Clean image presentation */}
                <div className="relative group">
                  <img 
                    src="/images/parallax-surround-sound-gray.png"
                    alt="Parallax Immersion 5.1 Surround Sound System"
                    className="w-full h-auto rounded-lg"
                  />
                  
                  {/* Subtle hover effect overlay */}
                  <div className="absolute inset-0 bg-gradient-radial from-highlight/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none"></div>
                </div>
                
                {/* Premium caption styling */}
                <motion.div 
                  className="mt-8 text-center"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  <div className="text-highlight/60 text-xs uppercase tracking-[0.3em] mb-2">
                    Handcrafted Excellence
                  </div>
                  <div className="text-secondary/40 text-sm uppercase tracking-wider">
                    Reference-Quality Acoustics
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* SPEAKER CONFIGURATION SHOWCASE */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-light mb-6 text-gradient">
                5.1 Channel Mastery
              </h2>
              <p className="text-xl text-secondary/80 max-w-3xl mx-auto">
                Each speaker meticulously positioned and calibrated to create the perfect acoustic environment
              </p>
            </motion.div>
            
            {/* Speaker configuration visualization */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-16">
              {/* Front Left */}
              <motion.div 
                className="glass-effect rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-highlight text-xl">L</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Front Left</h3>
                <p className="text-sm text-secondary/80">Precision dynamics</p>
              </motion.div>
              
              {/* Center Channel */}
              <motion.div 
                className="glass-effect rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-highlight text-xl">C</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Center</h3>
                <p className="text-sm text-secondary/80">Crystal dialogue</p>
              </motion.div>

              {/* Front Right */}
              <motion.div 
                className="glass-effect rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-highlight text-xl">R</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Front Right</h3>
                <p className="text-sm text-secondary/80">Matched imaging</p>
              </motion.div>

              {/* Surround Left */}
              <motion.div 
                className="glass-effect rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-highlight text-xl">SL</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Surround L</h3>
                <p className="text-sm text-secondary/80">Spatial immersion</p>
              </motion.div>

              {/* Surround Right */}
              <motion.div 
                className="glass-effect rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-highlight text-xl">SR</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Surround R</h3>
                <p className="text-sm text-secondary/80">Ambient detail</p>
              </motion.div>

              {/* Subwoofer */}
              <motion.div 
                className="glass-effect rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-highlight text-xl">⬛</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Subwoofer</h3>
                <p className="text-sm text-secondary/80">Deep foundation</p>
              </motion.div>
            </div>
            
            {/* Key acoustic features */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-3xl font-light mb-4">20Hz - 40kHz</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Reference Frequency Response</h3>
                <p className="text-secondary/80">
                  Every frequency reproduced with surgical precision, from the deepest bass 
                  to the most delicate harmonics.
                </p>
              </motion.div>
              
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-3xl font-light mb-4">92dB @ 1W/1m</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">High Sensitivity</h3>
                <p className="text-secondary/80">
                  Effortless dynamics that bring every performance to life with 
                  breathtaking realism and impact.
                </p>
              </motion.div>

              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-3xl font-light mb-4">Premium Hardwood</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Handcrafted Cabinets</h3>
                <p className="text-secondary/80">
                  Each cabinet is individually crafted from premium hardwood veneers, 
                  chosen for both acoustic and aesthetic excellence.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-light mb-8 text-gradient">
                Experience Acoustic Perfection
              </h2>
              <p className="text-xl text-secondary/80 mb-12 leading-relaxed">
                Visit our private listening room or schedule an acoustic consultation 
                to experience the transformative power of the Immersion system. 
                Discover how handcrafted acoustic artistry can transform your space.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center max-w-2xl mx-auto">
                <button 
                  onClick={openFormPopup}
                  className="btn-primary text-lg px-12 py-4 flex-1 sm:flex-none whitespace-nowrap"
                >
                  Schedule Listening Session
                </button>
                <button 
                  onClick={openFormPopup}
                  className="btn-secondary text-lg px-12 py-4 flex-1 sm:flex-none whitespace-nowrap"
                >
                  Request Acoustic Analysis
                </button>
              </div>

              <div className="mt-12 text-center">
                <p className="text-secondary/60 text-sm mb-4">
                  Custom finishes and room-specific calibration included
                </p>
                <div className="inline-flex items-center gap-4 text-xs text-secondary/40 uppercase tracking-wider">
                  <span>Handcrafted Excellence</span>
                  <span>•</span>
                  <span>Bespoke Installation</span>
                  <span>•</span>
                  <span>Acoustic Optimization</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Popup handled globally */}
    </div>
  );
};

export default ImmersionSurround; 